import orderBy from "lodash/orderBy";

import labels from "../../../constants/displayFields";
import manufacturers from "../../../constants/manufacturers";
import useLabels from "../../../hooks/useLabels";
import { getOptions } from "../../../utils/form";
import colors from "../../../constants/colors";
import dispositions from "../../../constants/dispositions";
import unitOfMeasureOptions from "../../../constants/fields/unitOfMeasure";
import { formatDays } from "../../../utils/dates";
import { predefinedCubicFeetOptions } from "../../../constants/asset";
import { getSquareFootageTypeOptions } from "../../../utils/squareFootageHelpers";

const section = "descriptive";

export default function useEnabledDescriptive({
  display,
  manufacturersOptions,
  colorsOptions,
  dispositionOptions,
  type,
  asset,
  handleUpdateAssetField,
}) {
  const l = useLabels();

  const fields = [
    {
      id: "manufacturer",
      enabled: display.manufacturer,
      type: "autocomplete",
      section,
      params: {
        name: "manufacturer",
        label: labels.manufacturer,
        options: orderBy(getOptions(manufacturers, manufacturersOptions), v =>
          v.value?.toLowerCase()
        ),
        saveCustomOption: true,
      },
    },
    {
      id: "description",
      enabled: display.description,
      type: "input",
      section,
      params: {
        name: "description",
        type: "autoExpand",
        label: labels.description,
        showNAButton: false,
      },
    },
    {
      id: "description_comments",
      enabled: display.description_comments,
      type: "input",
      section,
      params: {
        name: "descriptionComments",
        type: "autoExpand",
        label: l.descriptiveInfo.description_comments,
      },
    },
    {
      id: "model",
      enabled: display.model,
      type: "input",
      section,
      params: {
        name: "model",
        type: "autoExpand",
        label: l.descriptiveInfo.model,
      },
    },
    {
      id: "dimensions",
      enabled: display.dimensions,
      type: "dimensions",
      section,
      params: {
        name: "dimensions",
        asset: asset,
        handleUpdateAssetField: handleUpdateAssetField,
      },
    },
    {
      id: "color",
      enabled: display.color,
      type: "autocomplete",
      section,
      params: {
        name: "color",
        label: labels.color,
        options: orderBy(getOptions(colors, colorsOptions), v => v.value),
        saveCustomOption: true,
      },
    },
    {
      id: "condition",
      enabled: display.condition,
      type: "input",
      section,
      params: {
        type: "autoExpand",
        name: "condition",
        label: labels.condition,
      },
    },
    {
      id: "finishes",
      enabled: display.finishes,
      type: "finishes",
      section,
      params: {
        name: "finishes",
      },
    },
    {
      id: "disposition",
      enabled: display.disposition,
      type: "autocomplete",
      section,
      params: {
        name: "disposition",
        label: labels.disposition,
        options: orderBy(getOptions(dispositions, dispositionOptions), v => v.value),
        saveCustomOption: true,
      },
    },
    {
      id: "category",
      enabled: display.category,
      type: "category",
      section,
      params: {
        name: "category",
      },
    },
    {
      id: "subcategory",
      enabled: display.subcategory,
      type: "subcategory",
      section,
      params: {
        name: "subcategory",
      },
    },
    {
      id: "unit_cost",
      enabled: display.unit_cost,
      type: "input",
      section,
      params: {
        name: "unitCost",
        type: "number",
        step: "0.01",
        label: labels.unit_cost,
        showNAButton: false,
      },
    },
    {
      id: "manufacturePartNumber",
      enabled: display.manufacture_part_number,
      type: "input",
      section,
      params: {
        name: "manufacturePartNumber",
        type: "autoExpand",
        label: l.descriptiveInfo.manufacture_part_number,
      },
    },
    {
      id: "unitOfMeasure",
      enabled: display.unit_of_measure,
      type: "autocomplete",
      section,
      params: {
        name: "unitOfMeasure",
        options: unitOfMeasureOptions,
        label: l.descriptiveInfo.unit_of_measure,
        saveCustomOption: true,
        showNAButton: false,
      },
    },
    {
      id: "expirationDate",
      enabled: display.expiration_date,
      type: "monthYearPicker",
      section,
      params: {
        name: "expirationDate",
        label: l.descriptiveInfo.expiration_date,
      },
    },
    {
      id: "expirationMonthDayYear",
      enabled: display.expiration_month_day_year,
      type: "monthDayYearPicker",
      section,
      params: {
        name: "expirationMonthDayYear",
        label: l.descriptiveInfo.expiration_month_day_year,
      },
    },
    {
      id: "manufactureDate",
      enabled: display.manufacture_date,
      type: "monthDayYearPicker",
      section,
      params: {
        name: "manufactureDate",
        label: l.descriptiveInfo.manufacture_date,
      },
    },
    {
      id: "weight",
      enabled: display.weight,
      type: "weightField",
      section,
      params: {
        name: "weight",
        label: labels.weight,
        asset,
        handleUpdateAssetField,
        inputProps: { inputMode: "decimal" },
      },
    },
    {
      id: "assetTotalWeight",
      enabled: display.asset_total_weight,
      type: "input",
      section,
      params: {
        name: "assetTotalWeight",
        type: "autoExpand",
        label: l.descriptiveInfo.asset_total_weight,
        showNAButton: false,
      },
    },
    {
      id: "expirationMonth",
      enabled: display.expiration_month,
      type: "monthPicker",
      section,
      params: {
        name: "expirationMonth",
        label: l.descriptiveInfo.expiration_month,
      },
    },
    {
      id: "expirationYear",
      enabled: display.expiration_year,
      type: "yearPicker",
      section,
      params: {
        name: "expirationYear",
        label: l.descriptiveInfo.expiration_year,
      },
    },
    {
      id: "daysLeftToExpire",
      enabled: display.days_left_to_expire,
      type: "input",
      section,
      params: {
        name: "daysLeftToExpire",
        type: "autoExpand",
        label: labels.days_left_to_expire,
        value: asset?.daysLeftToExpire ? formatDays(asset.daysLeftToExpire) : "",
      },
    },
    {
      id: "importedCustomAttributes",
      enabled: display.imported_custom_attributes,
      type: "input",
      section,
      params: {
        name: "importedCustomAttributes",
        type: "autoExpand",
        label: "Imported Custom Attributes",
      },
    },
    {
      id: "squareFootage",
      enabled: display.square_footage,
      type: "input",
      section,
      params: {
        name: "squareFootage",
        type: "autoExpand",
        label: labels.square_footage,
        showNAButton: false,
      },
    },
    {
      id: "squareFootageType",
      enabled: display.square_footage_type,
      type: "select",
      section,
      params: {
        name: "squareFootageType",
        label: labels.square_footage_type,
        options: getSquareFootageTypeOptions(asset?.project?.squareFootageTypes),
        showNAButton: false,
      },
    },
    {
      id: "cubicFootage",
      enabled: display.cubic_footage,
      type: "input",
      section,
      params: {
        name: "cubicFootage",
        type: "autoExpand",
        label: labels.cubic_footage,
        showNAButton: false,
      },
    },
    {
      id: "predefinedCubicFeet",
      enabled: display.predefined_cubic_feet,
      type: "select",
      section,
      params: {
        name: "predefinedCubicFeet",
        options: predefinedCubicFeetOptions,
        label: labels.predefined_cubic_feet,
        showNAButton: false,
      },
    },
  ].filter(v => v.enabled);

  return fields;
}
